/**
 * @file useUpdate.js
 * @module src/js/hooks/useUpdate
 * @description
 * **A hook to update existing items via PUT request.**
 * - Utilizes React contexts to manage modal messages.
 * - Returns a function that:
 *   - Sends updated data (with or without files) to the backend.
 *   - Handles both FormData (for file uploads) and JSON payloads.
 *   - Updates the local data state upon successful update.
 *   - Utilizes modal messages to indicate update status.
 * 
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUpdate hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useUser } from "../contexts/UserProvider";
import { useModal } from "../contexts/ModalProvider";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useUpdate({
    setData,
    setUpdating,
    setUpdatedID, // setUpdatedID is optional (uploading: not needed)
    setShowUploadForm,
}) {
    const { uid } = useUser();
    const { showModalMessage } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setData !== "function" ||
        typeof setUpdating !== "function" ||
        typeof setShowUploadForm !== "function"
    ) {
        throw new Error("setData, setUpdating, and setShowUploadForm functions are required.");
    }

    const update = useCallback(async ({
        request,
        body,
        files = [],
        from,
        scrollTo,
    }) => {
        if (uid === 'Guest') {
            throw new Error("Guests are not allowed to update items.");
        }
        if (!request) {
            throw new Error("Request endpoint must be provided.");
        }
        if (!body || body.id == null) {
            throw new Error("body with id must be provided to update an item.");
        }
        if (typeof setUpdating !== "function") {
            throw new Error("setUpdating function is required.");
        }

        try {
            setUpdating(true);
            showModalMessage({ type: 'info', content: 'Updating...' });

            body.uploader = uid;
            let formData = new FormData();
            if (files.length > 0) {
                Object.entries(body).forEach(([key, value]) => {
                    formData.append(key, value);
                });

                // Append files in the "file[]" field
                files.forEach(file => {
                    if (file) formData.append("file[]", file);
                });
            }

            const data = await fetchFromBackend({
                request,
                method: "PUT",
                body: files.length > 0 ? formData : body,
                uploadFile: files.length > 0,
                requireAuth: true,
                from,
                scrollTo,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request });
                setData(data || []);
                setShowUploadForm(false);
                setUpdatedID(-1);
                showModalMessage({ type: "success", content: "Successfully updated!" });

            } else if (data && data.error) {
                console.error("Failed to update:", data.error);
                showModalMessage({ type: "error", content: "Failed to update. Please try again." });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });

        } finally {
            setUpdating(false);
        }

    }, [fetchFromBackend, uid, setData, setUpdating, setUpdatedID, setShowUploadForm, showModalMessage]);

    return update;
}